/**
 * Toasts del Admin (design-system §7 · Toast): esquina superior derecha, fondo marino, texto
 * crema, icono semántico, 4 s (8 s con acción). Sobre Radix Toast.
 *
 * Además escucha `fermin:read-only` (lo emite `api/client.ts` ante `423 READ_ONLY`) para avisar
 * que la empresa está suspendida y en solo lectura.
 *
 * Consulta en pruebas (Polish B): `ToastPrimitive.Provider` de Radix, aparte del `<li>` visible
 * que arma este componente (icono + `Title`/`Description`), monta su propia región
 * `role="status" aria-live="assertive"` oculta (`sr-only`) que anuncia el mismo texto con el
 * prefijo "Notification" — es el mecanismo estándar de Radix para lectores de pantalla, no una
 * duplicación nuestra, y no hay que "arreglarlo". Un matcher **exacto** (`getByText('texto')`) no
 * choca contra esa región (su contenido lleva el prefijo). Si una prueba necesita un matcher
 * parcial o `RegExp`, usa el helper que sí puede matchear ambos nodos y quedarse con el primero:
 * `tests/e2e/helpers.ts#toastText` en Playwright, `tests/unit/toast.ts#toastText` en Vitest.
 */
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { READ_ONLY_EVENT, type ApiErrorEventDetail } from '../../api/client';
import { t } from '../../i18n';
import { cn } from '../../lib/cn';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
}

interface ToastItem extends ToastOptions {
  id: number;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleAlert,
} as const;

const ICON_CLASSES: Record<ToastVariant, string> = {
  info: 'text-fm-info',
  success: 'text-fm-success',
  warning: 'text-fm-warning',
  error: 'text-fm-danger',
};

export const TOAST_DURATION_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((options: ToastOptions) => {
    setItems((current) => [...current, { ...options, id: Date.now() + current.length }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    function onReadOnly(event: Event) {
      const detail = (event as CustomEvent<ApiErrorEventDetail>).detail;
      toast({
        title: t('common.banners.suspended'),
        description: detail?.message,
        variant: 'warning',
      });
    }
    window.addEventListener(READ_ONLY_EVENT, onReadOnly);
    return () => window.removeEventListener(READ_ONLY_EVENT, onReadOnly);
  }, [toast]);

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider duration={TOAST_DURATION_MS} swipeDirection="right">
        {children}
        {items.map((item) => {
          const variant: ToastVariant = item.variant ?? 'info';
          const Icon = ICONS[variant];
          return (
            <ToastPrimitive.Root
              key={item.id}
              onOpenChange={(open) => {
                if (!open) {
                  dismiss(item.id);
                }
              }}
              className={cn(
                'flex items-start gap-fm-2 rounded-fm-md bg-fm-text p-fm-4 shadow-fm-2',
                'text-fm-sm text-fm-text-inverse',
              )}
            >
              <Icon size={20} aria-hidden="true" className={ICON_CLASSES[variant]} />
              <div className="min-w-0">
                <ToastPrimitive.Title className="font-semibold">{item.title}</ToastPrimitive.Title>
                {item.description ? (
                  <ToastPrimitive.Description className="opacity-90">
                    {item.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport className="fixed right-fm-4 top-fm-4 z-50 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-fm-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

/** Hook para lanzar toasts. Debe usarse dentro de `<ToastProvider>`. */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast debe usarse dentro de <ToastProvider>');
  }
  return context;
}
